import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import PropTypes from 'prop-types'
import { IFButton, IFText, IFTextInput } from 'Components'
import React, { useState, useImperativeHandle } from 'react'
import { Colors } from 'Theme'
import { useTranslation } from 'react-i18next'

import CloseIcon from '@mui/icons-material/Close'
import Icon from '@mui/material/Icon'
import Styles from './IFDialog.module.css'
import { styled as muiStyled } from '@mui/material/styles'

const StyledDialog = muiStyled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'dialogWidth',
})(({ theme, dialogWidth }) => ({
  '& .MuiPaper-root': {
    overflowY: 'unset',
    borderRadius: '5px',
    paddingBottom: '1rem',
    width: dialogWidth ? dialogWidth : 'unset',
    '& .MuiDialogContent-root': {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: '0.5rem',
      overflow: 'unset',
    },
  },
}))

const IFDialog = React.forwardRef(
  (
    {
      style,
      dialogWidth,
      title,
      titleStyle = { FontSize: '1.25rem' },
      subtitle,
      onAction,
      actionButtonText,
      actionButtonColor,
      actionButtonIcon,
      confirmationText,
      buttonClassName,
      isLoading,
      isDead,
      children,
      onDismiss = () => {},
      hasFirstButton = true,
      hasSecondButton = false,
      secondActionButtonText = '',
      secondActionButtonColor = '',
      secondActionButtonIcon = '',
      secondActionButtonOnClick = () => {},
      isSecondButtonDead = false,
      isSecondButtonLoading = false,
      animateButtons = false,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [buttonIsDead, setButtonIsDead] = useState(true)
    const [firstButtonClicked, setFirstButtonClicked] = useState(false)
    const [secondButtonClicked, setSecondButtonClicked] = useState(false)

    const updateInputValue = (event) => {
      if (confirmationText)
        if (event.target.value === confirmationText) setButtonIsDead(false)
        else setButtonIsDead(true)
      setInputValue(event.target.value)
    }

    const show = () => {
      setIsOpen(true)
    }

    const dismiss = () => {
      setIsOpen(false)
      setInputValue('')
      setButtonIsDead(true)
      onDismiss()
    }

    const resetAnimation = () => {
      setFirstButtonClicked(false)
      setSecondButtonClicked(false)
    }

    useImperativeHandle(ref, () => ({
      show,
      dismiss,
      resetAnimation,
    }))

    return (
      <div>
        <StyledDialog
          style={style}
          open={isOpen}
          onClose={dismiss}
          dialogWidth={dialogWidth}
        >
          <DialogTitle>
            <IFText style={titleStyle}>{title}</IFText>
          </DialogTitle>
          <DialogContent className={Styles.DialogContentStyle}>
            <div className={Styles.subtitleContainer}>
              {subtitle && (
                <IFText
                  className={Styles.subtitleText}
                  style={{ color: Colors.text }}
                >
                  {subtitle}
                </IFText>
              )}
              {children ? children : null}
            </div>
            {confirmationText ? (
              <IFTextInput
                className={Styles.IFTextInputStyle}
                value={inputValue}
                onChange={updateInputValue}
                isFixed={true}
              />
            ) : null}
          </DialogContent>
          <DialogActions sx={{ marginRight: '8px' }}>
            <IFButton
              onClick={dismiss}
              size="xsm"
              icon={
                <Icon
                  sx={{ width: '15px !important', height: '15px !important' }}
                  component={CloseIcon}
                />
              }
              color={Colors.primary}
              text={t('IFDialog.Cancel')}
              className={buttonClassName}
              isFill={false}
            />
            {hasFirstButton ? (
              <div
                className={
                  secondButtonClicked && animateButtons
                    ? Styles.ButtonFade
                    : Styles.ButtonVisible
                }
              >
                <IFButton
                  onClick={() => {
                    if (hasSecondButton) {
                      setFirstButtonClicked(true)
                    }
                    onAction()
                  }}
                  color={actionButtonColor}
                  isDead={confirmationText ? buttonIsDead : isDead}
                  isLoading={secondButtonClicked ? false : isLoading}
                  isFill={true}
                  size="xsm"
                  icon={actionButtonIcon}
                  autoFocus
                  text={actionButtonText}
                  className={buttonClassName}
                />
              </div>
            ) : null}

            {hasSecondButton ? (
              <div
                className={
                  firstButtonClicked && animateButtons
                    ? Styles.ButtonFade
                    : Styles.ButtonVisible
                }
              >
                <IFButton
                  onClick={() => {
                    if (hasFirstButton) {
                      setSecondButtonClicked(true)
                    }
                    secondActionButtonOnClick()
                  }}
                  size="xsm"
                  color={secondActionButtonColor}
                  isDead={isSecondButtonDead}
                  text={secondActionButtonText}
                  className={buttonClassName}
                  isLoading={firstButtonClicked ? false : isSecondButtonLoading}
                  isFill={true}
                  icon={secondActionButtonIcon}
                  autoFocus
                />
              </div>
            ) : null}
          </DialogActions>
        </StyledDialog>
      </div>
    )
  },
)

IFDialog.propTypes = {
  style: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.any,
  onAction: PropTypes.func,
  actionButtonText: PropTypes.string,
  actionButtonColor: PropTypes.string,
  buttonCancelText: PropTypes.string,
  actionButtonIcon: PropTypes.element,
  buttonClassName: PropTypes.string,
  confirmationText: PropTypes.string,
  dialogWidth: PropTypes.string,
}

export default IFDialog
